import * as z from "zod";
import {
  dropConnectorUsage,
  recordConnectorUsage,
  usageRef,
} from "../connectorUsages";
import { zql } from "../generatedDrizzleSchema";
import { assertAccess } from "../rbac/access";
import { mutator } from "./mutator";

export const updateAuditLogDestinationSchema = z.object({
  id: z.string(),
  name: z.string().min(1).optional(),
  url: z.string().url().startsWith("https://").optional(),
  method: z.enum(["PUT", "POST"]).optional(),
  connectedAppId: z.string().nullable().optional(),
});

export const updateAuditLogDestination = mutator({
  schema: updateAuditLogDestinationSchema,
  resolve: {
    destination: {
      kind: "auditLogDestination",
      from: "id",
    },
    app: {
      kind: "connectedApp",
      from: "connectedAppId",
      optional: true,
    },
  },
  permissions: [
    {
      permission: "tenant:audit_logs_manage",
      scopeId: ({ ctx }) => ctx.tenantId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const id = args.destination.id;

    const ref = usageRef.auditLogDestination(id);
    const [existingUsage] = await tx.run(
      zql.connectedAppUsages
        .where("type", ref.type)
        .where("refId", ref.refId)
        .where("tenantId", ctx.tenantId)
    );
    const currentAppId = existingUsage?.connectedAppId ?? null;

    // Re-check connected_app:use only when the binding is changing — editing
    // name/URL doesn't require continued rights on the existing app.
    const rebinding =
      args.connectedAppId !== undefined && args.connectedAppId !== currentAppId;
    if (rebinding && args.app) {
      // principal-exempt: not exposed on any public API route — reachable only
      // via the browser /zero/mutate path, so ctx.principal is never a service
      // account here.
      await assertAccess({
        tx,
        userId: ctx.userId,
        scopeId: args.app.id,
        tenantId: ctx.tenantId,
        permission: "connected_app:use",
      });
    }

    await tx.mutate.auditLogDestinations.update({
      id,
      ...(args.name !== undefined && { name: args.name }),
      ...(args.url !== undefined && { url: args.url }),
      ...(args.method !== undefined && { method: args.method }),
    });

    if (rebinding) {
      if (currentAppId) {
        await dropConnectorUsage(tx, currentAppId, ref);
      }
      if (args.app) {
        await recordConnectorUsage(tx, args.app.id, ref, ctx.tenantId);
      }
    }
  },
});
